// PlanEditor — Owner-only Tabelle der Abo-Pläne (eve_subscription_plans).
// Pro Zeile editierbar: Name, Credits/Monat, Aktiv-Toggle. Speichern → PUT
// /admin/subscription-plans/:id. Preise + Stripe-Price-IDs sind read-only
// (an Stripe gebunden) und werden nur informativ angezeigt.
// Self-contained: lädt selbst via useAuth-Token, hält eigenen Draft-State.

import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/hooks/use-auth';
import {
  listSubscriptionPlans,
  updateSubscriptionPlan,
  type SubscriptionPlanRow,
} from '@/lib/admin-subscriptions-api';

interface PlanDraft {
  name: string;
  creditsPerMonth: string;
  active: boolean;
}

function toDraft(row: SubscriptionPlanRow): PlanDraft {
  return {
    name: row.name,
    creditsPerMonth: String(row.credits_per_month),
    active: row.active === 1,
  };
}

function draftEqualsRow(draft: PlanDraft, row: SubscriptionPlanRow): boolean {
  return (
    draft.name === row.name &&
    draft.creditsPerMonth === String(row.credits_per_month) &&
    draft.active === (row.active === 1)
  );
}

function formatEuro(cents: number): string {
  return (cents / 100).toLocaleString('de-DE', { maximumFractionDigits: 2 });
}

export function PlanEditor() {
  const { accessToken } = useAuth();
  const [rows, setRows] = useState<SubscriptionPlanRow[]>([]);
  const [drafts, setDrafts] = useState<Record<string, PlanDraft>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!accessToken) return;
    setLoading(true);
    try {
      const data = await listSubscriptionPlans(accessToken);
      setRows(data);
      setDrafts(Object.fromEntries(data.map((r) => [r.id, toDraft(r)])));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Laden fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    void reload();
  }, [reload]);

  function patchDraft(id: string, patch: Partial<PlanDraft>) {
    setDrafts((prev) => {
      const current = prev[id];
      if (!current) return prev;
      return { ...prev, [id]: { ...current, ...patch } };
    });
  }

  async function save(row: SubscriptionPlanRow) {
    if (!accessToken) return;
    const draft = drafts[row.id];
    if (!draft) return;
    const credits = Number.parseInt(draft.creditsPerMonth, 10);
    if (!draft.name.trim()) {
      setError(`${row.id}: Name ist Pflicht.`);
      return;
    }
    if (!Number.isFinite(credits) || credits < 0) {
      setError(`${draft.name}: Credits/Monat müssen ≥ 0 sein.`);
      return;
    }
    setSavingId(row.id);
    setError(null);
    try {
      await updateSubscriptionPlan(accessToken, row.id, {
        name: draft.name.trim(),
        credits_per_month: credits,
        active: draft.active,
        sort_order: row.sort_order,
      });
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Speichern fehlgeschlagen');
    } finally {
      setSavingId(null);
    }
  }

  return (
    <section className="glass-strong rounded-2xl p-5">
      <div className="mb-4">
        <h2 className="text-base font-semibold">Abo-Pläne</h2>
        <p className="text-[12px] text-neutral-500">
          Monatliche Credit-Allowance pro Abo. Preise sind an Stripe gebunden und
          hier nur informativ — Name, Credits/Monat und Aktiv-Status sind editierbar.
        </p>
      </div>

      {error ? (
        <div className="mb-3 rounded border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-[12px] text-rose-300">
          {error}
        </div>
      ) : null}

      {loading ? (
        <div className="py-8 text-center text-[12px] text-neutral-500">Lade Abo-Pläne…</div>
      ) : rows.length === 0 ? (
        <div className="py-8 text-center text-[12px] text-neutral-500">
          Keine Abo-Pläne hinterlegt.
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((row) => {
            const draft = drafts[row.id];
            if (!draft) return null;
            const dirty = !draftEqualsRow(draft, row);
            return (
              <div
                key={row.id}
                className="rounded-xl border border-white/5 bg-white/[0.02] p-4"
              >
                <div className="mb-3 flex items-center justify-between">
                  <span className="font-mono text-[10px] uppercase tracking-wider text-neutral-500">
                    {row.id}
                  </span>
                  <label className="flex items-center gap-2 text-[11px] text-neutral-400">
                    <input
                      type="checkbox"
                      checked={draft.active}
                      onChange={(e) => patchDraft(row.id, { active: e.target.checked })}
                      className="size-3.5 accent-amber-500"
                    />
                    Aktiv
                  </label>
                </div>

                <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
                  <PlanField label="Name">
                    <input
                      type="text"
                      value={draft.name}
                      onChange={(e) => patchDraft(row.id, { name: e.target.value })}
                      className="w-full rounded border border-white/10 bg-white/5 px-2 py-1 text-[12px] outline-none focus:border-amber-500/40"
                      aria-label={`Name für ${row.id}`}
                    />
                  </PlanField>
                  <PlanField label="Credits / Monat">
                    <input
                      type="number"
                      value={draft.creditsPerMonth}
                      min={0}
                      step={1}
                      onChange={(e) => patchDraft(row.id, { creditsPerMonth: e.target.value })}
                      className="w-full rounded border border-white/10 bg-white/5 px-2 py-1 text-[12px] tabular outline-none focus:border-amber-500/40"
                      aria-label={`Credits/Monat für ${row.id}`}
                    />
                  </PlanField>
                  <PlanField label="Preis (Stripe-gebunden)">
                    <div className="w-full rounded border border-white/5 bg-white/[0.02] px-2 py-1 text-[12px] tabular text-neutral-400">
                      {formatEuro(row.price_monthly_cents)} € / Monat ·{' '}
                      {formatEuro(row.price_yearly_cents)} € / Jahr
                    </div>
                  </PlanField>
                </div>

                <div className="mt-2 flex items-center justify-between">
                  <p className="text-[10px] italic text-neutral-500">
                    Preise + Stripe-Price-IDs werden über Stripe verwaltet und sind read-only.
                  </p>
                  <button
                    type="button"
                    onClick={() => void save(row)}
                    disabled={!dirty || savingId === row.id}
                    className="rounded-md border border-amber-500/40 bg-amber-500/15 px-3 py-1 text-[12px] font-semibold text-amber-200 hover:bg-amber-500/25 disabled:opacity-40"
                  >
                    {savingId === row.id ? 'Speichere…' : 'Speichern'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

function PlanField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-1 text-[10px] uppercase tracking-wider text-neutral-500">{label}</div>
      {children}
    </div>
  );
}
